import React from "react";
import AccentReadOnlyCheckbox from "../../../../../../common/FormFields/AccentReadOnlyCheckbox";
import GenerateStepPreview from "./GenerateStepPreview";
import ItemsRequiredTable from "./ItemsRequiredTable";
import RichText from "./Nodes/RichText";
import Attachment from "./Nodes/Attachment";
import Video from "./Nodes/Video";
import Canvas from "./Nodes/Canvas";
import RenderNodes from "./RenderNodes";
import HeadingComponent from "../HeadingComponent";
import HtmlOrPlainShortDescription from "../HtmlOrPlainShortDescription";
import { alignStepShortDescriptionOrdinalWithHeading } from "../../../utils/previewUtils";
import {
  getStepDisplayOrdinal,
  substepHeadingMatchesTitleMatch,
} from "../../../utils/sectionDisplayOrdinal";

function getSubstepSortKey(ss) {
  const d = ss?.detail || {};
  const vc = ss?.version_details?.version_content || {};
  const raw =
    ss?.sibling_order ??
    ss?.procedure_tree?.sibling_order ??
    d.sibling_order ??
    vc.sibling_order ??
    "0";
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? 0 : n;
}

function getSubstepStableId(ss) {
  return (
    ss?.tree_node_id ||
    ss?.detail?.sub_step_tree_node_id ||
    ss?.version_details?.version_content?.sub_step_tree_node_id ||
    ss?.sub_step_tree_node_id ||
    ss?.substep_tree_node_id ||
    ss?.substep_id_ref ||
    ""
  );
}

/** Sort by sibling_order so Substep 1A / 1B letters match visible order (same idea as sorted steps in PreviewContent). */
function sortedSubstepsForLabelRank(substeps) {
  if (!Array.isArray(substeps) || substeps.length < 2) return substeps || [];
  return [...substeps].sort((a, b) => {
    const oa = getSubstepSortKey(a);
    const ob = getSubstepSortKey(b);
    if (oa !== ob) return oa - ob;
    return substeps.indexOf(a) - substeps.indexOf(b);
  });
}

function substepDisplayLetterIndex(substep, substepsSorted) {
  if (!substep) return 0;
  const id = getSubstepStableId(substep);
  const idx = substepsSorted.findIndex(
    (s) =>
      s === substep ||
      (id && getSubstepStableId(s) === id) ||
      (!id && s === substep),
  );
  return idx >= 0 ? idx : 0;
}

const getNodeGlobalIndex = (nodeIndices,sectionIdx, stepIdx, childIdx, substepIdx=undefined) => {
    let found = undefined 

    if(substepIdx){
      found = nodeIndices.find(
        i => i.sectionIdx === sectionIdx && i.stepIdx === stepIdx && i.childIdx === childIdx && i.substepIdx === substepIdx
      );
    }else{
      found = nodeIndices.find(
        i => i.sectionIdx === sectionIdx && i.stepIdx === stepIdx && i.childIdx === childIdx
      );
    }
    return found ? found.globalIndex : null;
};

const Step = ({
  nodeIndices,
  step,
  stepCount,
  sectionIdx,
  sectionDisplayOrdinal,
  stepIdx,
  stepsInSection,
  showCheckbox,
  isChecked,
  onCheckboxChange,
  selectedNodes,
  sectionIsSelected = false,
  disabled = false,
  readOnlyCheckboxTitleMatch = null,
  /** When set (from audit action text), overrides tree sibling_order so labels match app bar for copy/move substep */
  auditSubstepHeading = null,
  auditStepOrdinalOverride = null,
  auditSourceSectionLabel = null,
  /** Audit review: label Step 1..N by visible order in section; move/copy preview keeps sibling_order for a lone step */
  preferRelativeStepOrdinal = false,
  procedureWideStepOrdinal = null,
  hideRichTextFieldLabels = false,
}) => {
  if (!step) return null;
  // Support both API format (version_details.version_content) and snapshot format (detail)
  const rawStepContent = step?.version_details?.version_content || step?.detail || step || {};
  /** S3 / audit snapshots often put BOM in past_step_data_json.bom_rows while step_data.parts_required is empty */
  const pastStepJson = rawStepContent.past_step_data_json || {};
  const stepData = rawStepContent.step_data || {};
  // Snapshot format: step_details in step_data; API format: section_details_preview at root
  const section_details_preview =
    rawStepContent.section_details_preview ??
    stepData.step_details ??
    pastStepJson.step_details ??
    (Array.isArray(rawStepContent.section_details)
      ? rawStepContent.section_details.map((s) => ({
          action: s.action,
          partNumber: s.part_ref,
          description: s.description ?? "",
          qty: s.qty,
          created_at: s.created_at,
        }))
      : undefined);
  const short_description =
    rawStepContent.short_description ??
    step?.detail?.short_description ??
    "";
  const BOM_table_for_step_title =
    rawStepContent.BOM_table_for_step_title ??
    stepData.bom_table_title_info?.category_name ??
    pastStepJson.bom_table_title_info?.category_name;
  const part_number_column_title =
    rawStepContent.part_number_column_title ??
    stepData.part_number_column_title_ref_info?.category_name ??
    pastStepJson.part_number_column_title_ref_info?.category_name;
  const part_details_preview = (() => {
    const primary =
      rawStepContent.part_details_preview ??
      stepData.parts_required ??
      pastStepJson.parts_required ??
      rawStepContent.part_details;
    if (Array.isArray(primary) && primary.length > 0) return primary;
    const bomRows = pastStepJson.bom_rows || stepData.bom_rows;
    if (Array.isArray(bomRows) && bomRows.length > 0) return bomRows;
    return Array.isArray(primary) ? primary : [];
  })();
  const pageTitle = rawStepContent.pageTitle ?? rawStepContent.step_title;
  const stepContent = rawStepContent;
  const stepId =
    step.tree_node_id ||
    stepContent.step_tree_node_id ||
    step.detail?.step_tree_node_id ||
    step.step_tree_node_id ||
    step.step_id_ref;
  /** Align with PreviewContent getStepCheckIds — selectedNodes may key entity id while tree_node_id is procedure or copy-* */
  const stepCheckIds = [
    step.tree_node_id,
    stepContent.step_tree_node_id,
    step.detail?.step_tree_node_id,
    step.step_tree_node_id,
    step.step_id_ref,
    pageTitle,
  ].filter(Boolean);
  const sectionNumForPath = (() => {
    if (
      sectionDisplayOrdinal != null &&
      !Number.isNaN(sectionDisplayOrdinal)
    ) {
      return sectionDisplayOrdinal;
    }
    if (
      typeof auditSourceSectionLabel === "string" &&
      /^Section\s+\d+$/i.test(auditSourceSectionLabel.trim())
    ) {
      const m = auditSourceSectionLabel.match(/(\d+)/);
      if (m) return parseInt(m[1], 10);
    }
    return sectionIdx + 1;
  })();
  const stepOrdinalFromTree = getStepDisplayOrdinal(
    step,
    stepIdx,
    stepCount,
    stepsInSection,
    preferRelativeStepOrdinal,
  );
  /** App bar override, else procedure-wide index when parent passes full snapshot (audit preview + review), else tree ordinal. */
  const stepNumber =
    typeof auditStepOrdinalOverride === "number" &&
    !Number.isNaN(auditStepOrdinalOverride)
      ? auditStepOrdinalOverride
      : typeof procedureWideStepOrdinal === "number" &&
          !Number.isNaN(procedureWideStepOrdinal)
        ? procedureWideStepOrdinal
        : stepOrdinalFromTree;
  const treepath = (
    <>
      Section {sectionNumForPath} &gt; Step {stepNumber}
    </>
  );

  const stepSubsteps = step.substeps || [];
  const substepsSortedForLetters = sortedSubstepsForLabelRank(stepSubsteps);
  const buildDefaultSubstepHeading = (substep) => {
    const fromNarrow = substep?.auditPreviewSubstepLetterIndex;
    const letterIdx =
      typeof fromNarrow === "number" &&
      !Number.isNaN(fromNarrow) &&
      fromNarrow >= 0
        ? fromNarrow
        : substepDisplayLetterIndex(substep, substepsSortedForLetters);
    return `Substep ${stepNumber}${String.fromCharCode(65 + letterIdx)}`;
  };

  const stepIsDirectlySelected =
    stepCheckIds.some((id) => selectedNodes?.[id]) || isChecked || false;
  const stepRowChecked = stepIsDirectlySelected || sectionIsSelected;
  const stepRowReadOnly = disabled || sectionIsSelected;

  const emphasizeStepReadOnlyCheckbox =
    disabled &&
    readOnlyCheckboxTitleMatch?.kind === "step" &&
    readOnlyCheckboxTitleMatch.n === stepNumber &&
    stepIsDirectlySelected;
  
  const handleCheckboxChange = () => {
    if (onCheckboxChange && stepId) {
      onCheckboxChange(stepId);
    }
  };

  const hideStepHeader = step.hideStepHeader === true;
  const stepChildren = step.children || [];

  const renderNodesWrap = (
    children,
    substepIdx,
    substepNodeSelected = false,
    pathForNodes = treepath,
  ) => {
    const mergedDisabled =
      disabled ||
      sectionIsSelected ||
      stepIsDirectlySelected ||
      (substepIdx !== undefined && substepNodeSelected);
    return (
      <RenderNodes
        nodeIndices={nodeIndices}
        children={children}
        sectionIdx={sectionIdx}
        stepIdx={stepIdx}
        substepIdx={substepIdx}
        treepath={pathForNodes}
        showCheckbox={showCheckbox}
        selectedNodes={selectedNodes}
        onCheckboxChange={onCheckboxChange}
        disabled={mergedDisabled}
        sectionIsSelected={sectionIsSelected}
        stepIsDirectlySelected={stepIsDirectlySelected}
        substepIsSelected={substepIdx !== undefined ? substepNodeSelected : false}
        readOnlyCheckboxTitleMatch={readOnlyCheckboxTitleMatch}
        hideRichTextFieldLabels={hideRichTextFieldLabels}
        stepDisplayOrdinal={stepNumber}
      />
    );
  };

  /** Audit preview: title already names the node — omit parent step/section/substep chrome. */
  if (hideStepHeader) {
    const ss0 = stepSubsteps[0];
    if (
      stepSubsteps.length === 1 &&
      ss0?.hideSubstepHeader === true &&
      (ss0.children || []).length > 0
    ) {
      const ss0Content =
        ss0?.version_details?.version_content || ss0?.detail || ss0 || {};
      const ss0SubstepCheckIds = [
        ss0.tree_node_id,
        ss0Content.sub_step_tree_node_id,
        ss0.sub_step_tree_node_id,
        ss0.substep_id_ref,
      ].filter(Boolean);
      const ss0SubstepSelected =
        ss0SubstepCheckIds.some((id) => selectedNodes?.[id]) || false;
      const useAuditSubstepHeadingFlat =
        typeof auditSubstepHeading === "string" &&
        auditSubstepHeading.trim().length > 0 &&
        Array.isArray(step.substeps) &&
        step.substeps.length === 1;
      const ss0Heading = useAuditSubstepHeadingFlat
        ? auditSubstepHeading.trim()
        : buildDefaultSubstepHeading(ss0);
      const pathWithSubstep = (
        <>
          {treepath} &gt; {ss0Heading}
        </>
      );
      return (
        <div
          style={{ marginTop: 0, marginBottom: "30px" }}
          className="header-spacing"
        >
          {renderNodesWrap(
            ss0.children || [],
            0,
            ss0SubstepSelected,
            pathWithSubstep,
          )}
        </div>
      );
    }
    if (stepChildren.length > 0 && stepSubsteps.length === 0) {
      return (
        <div
          style={{ marginTop: 0, marginBottom: "30px" }}
          className="header-spacing"
        >
          {renderNodesWrap(stepChildren, undefined, false)}
        </div>
      );
    }
    if (stepSubsteps.length > 0 && stepChildren.length === 0) {
      return (
        <>
          {stepSubsteps.map((substep, substepIdx) => {
            if (!substep) return null;
            const substepContent =
              substep?.version_details?.version_content ||
              substep?.detail ||
              substep ||
              {};
            const useAuditSubstepHeading =
              typeof auditSubstepHeading === "string" &&
              auditSubstepHeading.trim().length > 0 &&
              Array.isArray(step.substeps) &&
              step.substeps.length === 1;
            const heading = useAuditSubstepHeading
              ? auditSubstepHeading.trim()
              : buildDefaultSubstepHeading(substep);
            const substepTreepath = (
              <>
                {treepath} &gt; {heading}
              </>
            );
            const substepToggleId =
              substep.tree_node_id ||
              substepContent.sub_step_tree_node_id ||
              substep.sub_step_tree_node_id ||
              substep.substep_id_ref;
            const substepCheckIds = [
              substep.tree_node_id,
              substepContent.sub_step_tree_node_id,
              substep.sub_step_tree_node_id,
              substep.substep_id_ref,
            ].filter(Boolean);

            const handleSubstepCheckboxChange = () => {
              if (onCheckboxChange && substepToggleId) {
                onCheckboxChange(substepToggleId);
              }
            };

            const substepNodeSelected =
              substepCheckIds.some((id) => selectedNodes?.[id]) || false;
            const substepRowChecked =
              substepNodeSelected ||
              sectionIsSelected ||
              stepIsDirectlySelected;
            const substepRowReadOnly =
              disabled ||
              sectionIsSelected ||
              stepIsDirectlySelected;
            const emphasizeSubstepReadOnlyCheckbox =
              disabled &&
              substepHeadingMatchesTitleMatch(
                heading,
                readOnlyCheckboxTitleMatch,
              ) &&
              substepNodeSelected;

            return (
              <div
                style={{ marginTop: substepIdx === 0 ? 0 : "30px", marginBottom: "30px" }}
                key={"substep_" + substepIdx}
                className="header-spacing"
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                  {showCheckbox &&
                    (substepRowReadOnly ? (
                      <AccentReadOnlyCheckbox
                        readOnlyVisual
                        emphasizeChecked={emphasizeSubstepReadOnlyCheckbox}
                        checked={substepRowChecked}
                        style={{ marginTop: "2px" }}
                      />
                    ) : (
                      <input
                        type="checkbox"
                        checked={substepRowChecked}
                        onChange={handleSubstepCheckboxChange}
                        style={{
                          width: "18px",
                          height: "18px",
                          cursor: "pointer",
                          marginTop: "2px",
                          flexShrink: 0,
                        }}
                      />
                    ))}
                  <div style={{ flex: 1, width: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <h3 style={{ margin: 0 }}>{heading}</h3>
                      <div
                        style={{
                          color: "rgb(136, 136, 136)",
                          fontSize: "14px",
                          fontWeight: "normal",
                        }}
                      >
                        {substepTreepath}
                      </div>
                    </div>
                  </div>
                </div>
                <HtmlOrPlainShortDescription
                  value={substepContent.short_description}
                />
                {renderNodesWrap(
                  substep.children || [],
                  substepIdx,
                  substepNodeSelected,
                  substepTreepath,
                )}
              </div>
            );
          })}
        </>
      );
    }
  }

  return (
    <div style={{ marginTop: "30px", marginBottom:"30px" }} className="header-spacing">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        {showCheckbox &&
          (stepRowReadOnly ? (
            <AccentReadOnlyCheckbox
              readOnlyVisual
              emphasizeChecked={emphasizeStepReadOnlyCheckbox}
              checked={stepRowChecked}
              style={{ marginTop: "2px" }}
            />
          ) : (
            <input
              type="checkbox"
              checked={stepRowChecked}
              onChange={handleCheckboxChange}
              style={{
                width: "18px",
                height: "18px",
                cursor: "pointer",
                marginTop: "2px",
                flexShrink: 0,
                accentColor: "#0D6EFD",
                backgroundColor: stepRowChecked ? "#0D6EFD" : "#ffffff",
                borderColor: stepRowChecked ? "#0D6EFD" : "#d3d3d3",
                borderWidth: "1px",
                borderStyle: "solid",
              }}
            />
          ))}
        <div style={{ flex: 1, width: '100%' }}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <h3 style={{ margin: 0 }}>{`Step ${stepNumber}`}</h3>
            <div style={{ color: "rgb(136, 136, 136)", fontSize: "14px", fontWeight: "normal" }}>{treepath}</div>
          </div>
        </div>
      </div>
      <hr style={{ borderColor: '#d3d3d3', backgroundColor: '#d3d3d3', color: '#d3d3d3' }} />

      <HtmlOrPlainShortDescription
        value={alignStepShortDescriptionOrdinalWithHeading(
          short_description,
          stepNumber,
        )}
      />

      <GenerateStepPreview stepDetails={Array.isArray(section_details_preview) ? section_details_preview : []} />
      <ItemsRequiredTable
        tableTitle={BOM_table_for_step_title ?? ""}
        columnTitle={part_number_column_title ?? ""}
        parts_required={part_details_preview ?? []}
      />

      {renderNodesWrap(step.children, undefined, false)}

      {step.substeps?.map((substep, substepIdx) => {
        if (!substep) return null;
        const substepContent = substep?.version_details?.version_content || substep?.detail || substep || {};
        const useAuditSubstepHeading =
          typeof auditSubstepHeading === "string" &&
          auditSubstepHeading.trim().length > 0 &&
          Array.isArray(step.substeps) &&
          step.substeps.length === 1;
        const heading = useAuditSubstepHeading
          ? auditSubstepHeading.trim()
          : buildDefaultSubstepHeading(substep);
        const substepTreepath = (
          <>
            {treepath} &gt; {heading}
          </>
        );
        /** Procedure tree_node_id first — matches audit preview_tree_node_id; sub_step_tree_node_id is entity id */
        const substepToggleId =
          substep.tree_node_id ||
          substepContent.sub_step_tree_node_id ||
          substep.sub_step_tree_node_id ||
          substep.substep_id_ref;
        const substepCheckIds = [
          substep.tree_node_id,
          substepContent.sub_step_tree_node_id,
          substep.sub_step_tree_node_id,
          substep.substep_id_ref,
        ].filter(Boolean);

        const handleSubstepCheckboxChange = () => {
          if (onCheckboxChange && substepToggleId) {
            onCheckboxChange(substepToggleId);
          }
        };

        const substepNodeSelectedMain =
          substepCheckIds.some((id) => selectedNodes?.[id]) || false;
        const substepRowCheckedMain =
          substepNodeSelectedMain ||
          sectionIsSelected ||
          stepIsDirectlySelected;
        const substepRowReadOnlyMain =
          disabled ||
          sectionIsSelected ||
          stepIsDirectlySelected;
        const emphasizeSubstepReadOnlyCheckboxMain =
          disabled &&
          substepHeadingMatchesTitleMatch(
            heading,
            readOnlyCheckboxTitleMatch,
          ) &&
          substepNodeSelectedMain;

        return <div style={{ marginTop: "30px", marginBottom:"30px" }} key={"substep_"+substepIdx} className="header-spacing">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          {showCheckbox &&
            (substepRowReadOnlyMain ? (
              <AccentReadOnlyCheckbox
                readOnlyVisual
                emphasizeChecked={emphasizeSubstepReadOnlyCheckboxMain}
                checked={substepRowCheckedMain}
                style={{ marginTop: "2px" }}
              />
            ) : (
              <input
                type="checkbox"
                checked={substepRowCheckedMain}
                onChange={handleSubstepCheckboxChange}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  marginTop: "2px",
                  flexShrink: 0,
                }}
              />
            ))}
          <div style={{ flex: 1, width: '100%' }}>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
              <h3 style={{ margin: 0 }}>{heading}</h3>
              <div style={{ color: "rgb(136, 136, 136)", fontSize: "14px", fontWeight: "normal" }}>{substepTreepath}</div>
            </div>
          </div>
        </div>
        <HtmlOrPlainShortDescription value={substepContent.short_description} />
        {renderNodesWrap(
          substep.children || [],
          substepIdx,
          substepNodeSelectedMain,
          substepTreepath,
        )}
        </div>
        
      })}
    </div>
  );
};

export default Step;
